import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { useSWRConfig } from 'swr';

import MetaTags from '../../components/MetaTags';
import Loading from '../../components/Loading';
import Container from '../../components/Layout/Container';
import FullScreenBox from '../../components/Layout/FullScreenBox';
import { Heading2 } from '../../components/Heading';
import useMemberPerformanceProfile, {
  PERFORMANCE_ERROR,
  useSharedMemberPerformance,
  clearPerformanceCache,
  getPerformanceIdentity,
} from '../../hooks/useMemberPerformanceProfile';
import { getUser, removeUser } from '../../utils/cookie';

import AwayPeriods from './AwayPeriods';
import { ACCESS, getPerformanceAccess } from './access';
import Balance from './Balance';
import { Card } from './Card';
import ConsistencyIntensity from './ConsistencyIntensity';
import { DEFAULT_WINDOW_WEEKS, WINDOW_OPTIONS } from './constants';
import Evidence from './Evidence';
import PerformanceHeader from './Header';
import LiftCards from './LiftCards';
import MuscleBalance from './MuscleBalance';
import NextSession from './NextSession';
import Recap from './Recap';
import Sharing from './Sharing';
import Targets from './Targets';
import useProcessingPoll from './useProcessingPoll';
import WeeklyActivity from './WeeklyActivity';

const Message = ({ title, children }) => (
  <FullScreenBox>
    <Box sx={{ textAlign: 'center', px: 2, maxWidth: 560 }}>
      <Heading2 sx={{ fontSize: { xs: 26, tl: 32 } }}>{title}</Heading2>
      <Box sx={{ mt: 2 }}>{children}</Box>
    </Box>
  </FullScreenBox>
);

const SignInPrompt = ({ username, shared = false }) => {
  const { mutate } = useSWRConfig();

  // A 401 means the stored session is stale. Clear it, or /signin sees a user cookie and
  // redirects straight back here, forever. Also drop any cached profiles.
  useEffect(() => {
    removeUser();
    clearPerformanceCache(mutate);
  }, [mutate]);

  return (
    <Message
      title={shared ? 'Sign in to see shared performance' : 'Sign in to see your performance'}
    >
      <Typography sx={{ mb: 2 }}>
        {shared
          ? 'Sign in to see performance that members have shared.'
          : 'Your performance recap is private to your account.'}
      </Typography>
      <Button
        component={Link}
        to="/signin"
        state={{ from: `/member/${username}/performance` }}
        variant="contained"
      >
        Sign in
      </Button>
    </Message>
  );
};

const LiveRegion = ({ children }) => (
  <Box role="status" aria-live="polite" data-testid="perf-live-region" sx={visuallyHidden}>
    {children}
  </Box>
);

// Small supporting note shown above the recap. Pass `role` for announced notices.
const Notice = ({ role, children }) => (
  <Typography role={role} sx={{ mb: 2, fontSize: 14, opacity: 0.85 }}>
    {children}
  </Typography>
);

const PerformanceBody = ({ username, shared = false }) => {
  const [windowWeeks, setWindowWeeks] = useState(DEFAULT_WINDOW_WEEKS);
  // Exactly one source is active: the member's own profile, or another member's shared view.
  const own = useMemberPerformanceProfile(windowWeeks, { enabled: !shared });
  const other = useSharedMemberPerformance(shared ? username : '', windowWeeks);
  const { profile, isLoading, errorKind, refetch } = shared ? other : own;
  const { addAwayPeriod, deleteAwayPeriod, saveTargets, resetTargets, setSharing } = own;
  // One feedback message at a time across the targets and sharing cards: { source, type, text }.
  const [feedback, setFeedback] = useState(null);
  // An away-period write that came back 401 means the session expired.
  const [writeSignIn, setWriteSignIn] = useState(false);
  // A new sign in (or a different member) starts with a clean slate.
  const identity = getPerformanceIdentity(getUser());
  useEffect(() => {
    setWriteSignIn(false);
    setFeedback(null);
  }, [identity, username]);

  const processing = profile?.dataQuality?.processing === true;
  const { pollsExhausted } = useProcessingPoll({ processing, errorKind, windowWeeks, refetch });

  // Choosing the window already in state (e.g. after its fetch failed) cannot change the swr
  // key, so refetch it explicitly or the control would seem to ignore the choice.
  const handleWindowChange = (weeks) => {
    if (weeks === windowWeeks) {
      refetch();
    } else {
      setWindowWeeks(weeks);
    }
  };

  if (writeSignIn || errorKind === PERFORMANCE_ERROR.SIGN_IN) {
    return (
      <>
        <LiveRegion>
          {shared ? 'Sign in to see shared performance.' : 'Sign in to see your performance.'}
        </LiveRegion>
        <SignInPrompt username={username} shared={shared} />
      </>
    );
  }
  // Not shared (or no such member): a calm, friendly page. Also covers sharing being turned
  // off while the page is open.
  if (shared && errorKind === PERFORMANCE_ERROR.NOT_FOUND) {
    return (
      <>
        <LiveRegion>This member has not shared their performance.</LiveRegion>
        <Message title="This member has not shared their performance">
          <Typography>
            Members choose whether to share their performance page. Nothing is wrong on your end.
          </Typography>
          <Button component={Link} to={`/member/${username}`} sx={{ mt: 2 }}>
            Back to profile
          </Button>
        </Message>
      </>
    );
  }
  if (errorKind && !profile) {
    const subject = shared ? "this member's performance" : 'your performance';
    return (
      <>
        <LiveRegion>We could not load {subject}.</LiveRegion>
        <Message title={`We could not load ${subject}`}>
          <Typography sx={{ mb: 2 }}>
            Something went wrong on our side. Please try again.
          </Typography>
          <Button variant="outlined" onClick={() => refetch()}>
            Try again
          </Button>
        </Message>
      </>
    );
  }
  if (!profile) {
    return (
      <>
        <LiveRegion>
          {shared ? "Loading this member's performance." : 'Loading your performance.'}
        </LiveRegion>
        <Loading fullscreen />
      </>
    );
  }

  // While a refresh has failed, show the window of the data actually on screen.
  const shownWeeks =
    errorKind && WINDOW_OPTIONS.includes(profile.windowWeeks) ? profile.windowWeeks : windowWeeks;
  const isEmpty = profile.dataQuality?.level === 'none';
  const ownerName = profile.owner?.username || username;
  const subjectPossessive = shared ? "this member's" : 'your';
  return (
    <Container
      sx={{ boxSizing: 'border-box', px: { xs: 2, tl: 0 }, py: { xs: 3, tl: 5 }, color: 'white' }}
    >
      <LiveRegion>{feedback?.type === 'status' ? feedback.text : ''}</LiveRegion>
      <PerformanceHeader
        title={shared ? `${ownerName}'s Performance` : undefined}
        sharedBy={shared ? ownerName : undefined}
        headline={profile.recap?.headline}
        asOf={profile.asOf}
        windowWeeks={shownWeeks}
        onWindowChange={handleWindowChange}
        isUpdating={isLoading && !errorKind}
      />
      {errorKind && (
        <Box
          role="alert"
          sx={{
            mb: 2,
            fontSize: 14,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <span>Could not refresh this view. Showing the last results.</span>
          <Button size="small" variant="outlined" onClick={() => refetch()}>
            Try again
          </Button>
        </Box>
      )}
      {/* While a refresh error shows, its alert announces; the banner stays quiet. */}
      {processing && (
        <Notice role={errorKind ? undefined : 'status'}>
          {`We are still processing ${subjectPossessive} full session history, so these numbers may be incomplete. ${
            errorKind
              ? 'Automatic refresh is paused. Use Try again to refresh.'
              : pollsExhausted
              ? 'Check back in a few minutes.'
              : 'This page will update on its own.'
          }`}
        </Notice>
      )}
      {isEmpty ? (
        <Card>
          <Typography variant="h2" sx={{ fontSize: 20, fontWeight: 700 }}>
            {shared
              ? `${ownerName} has no sessions in this window yet`
              : 'No sessions yet in this window'}
          </Typography>
          {!shared && (
            <Typography sx={{ mt: 1 }}>
              Play a few sessions, or try a longer time window, and your trainer's recap will show
              up here.
            </Typography>
          )}
        </Card>
      ) : (
        <>
          {profile.dataQuality?.level === 'low' && (
            <Notice>Still early days, so treat these ratings as a first look.</Notice>
          )}
          {shared && (
            <Notice>{`These notes are written for ${ownerName}; "you" means them.`}</Notice>
          )}
          <Recap
            recap={profile.recap}
            exercises={profile.exercises}
            muscleGroups={profile.muscleGroups}
          />
          <WeeklyActivity weeks={profile.weeks} />
          <LiftCards exercises={profile.exercises} weeks={profile.weeks} />
          <MuscleBalance muscleGroups={profile.muscleGroups} />
          <Balance balance={profile.balance} />
          <ConsistencyIntensity consistency={profile.consistency} intensity={profile.intensity} />
        </>
      )}
      {/* Older backends omit these fields; each card is hidden until its data exists. The
          shared view is read-only and never shows private items, even if sent. */}
      {!shared && profile.nextSession && !isEmpty && (
        <NextSession nextSession={profile.nextSession} />
      )}
      {!shared && profile.targets && (
        <Targets
          targets={profile.targets}
          targetProgress={profile.targetProgress}
          onSave={saveTargets}
          onReset={resetTargets}
          onSignIn={() => setWriteSignIn(true)}
          feedback={feedback}
          setFeedback={setFeedback}
        />
      )}
      {!shared && profile.sharing && (
        <Sharing
          sharing={profile.sharing}
          username={username}
          onSetSharing={setSharing}
          onSignIn={() => setWriteSignIn(true)}
          feedback={feedback}
          setFeedback={setFeedback}
        />
      )}
      {!shared && Array.isArray(profile.awayPeriods) && (
        <AwayPeriods
          awayPeriods={profile.awayPeriods}
          onAdd={addAwayPeriod}
          onDelete={deleteAwayPeriod}
          onSignIn={() => setWriteSignIn(true)}
        />
      )}
      <Evidence evidence={profile.evidence} notes={profile.dataQuality?.notes} />
    </Container>
  );
};

const MemberPerformance = () => {
  const { username } = useParams();
  const access = getPerformanceAccess(getUser(), username);

  return (
    <Box>
      {access === ACCESS.VIEWER ? (
        <MetaTags
          title={`Black Box VR - ${username}'s Performance`}
          description={`${username}'s training recap from Black Box VR.`}
        />
      ) : (
        <MetaTags
          title="Black Box VR - My Performance"
          description="Your personal training recap from Black Box VR."
        />
      )}
      {access === ACCESS.SIGNED_OUT && <SignInPrompt username={username} />}
      {access === ACCESS.VIEWER && <PerformanceBody key={username} username={username} shared />}
      {access === ACCESS.ALLOWED && <PerformanceBody username={username} />}
    </Box>
  );
};

export default MemberPerformance;
